import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import apiService from '../services/apiService';
import EmailList from './EmailList';
import useAutoCache from '../hooks/useAutoCache';

const MailView = () => {
  const [emails, setEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, unread, read, flagged
  const [sortBy, setSortBy] = useState('received'); // received, subject, from
  const [sortDirection, setSortDirection] = useState('desc'); // desc, asc
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadEmails();
  }, []);

  useEffect(() => {
    filterAndSortEmails();
  }, [emails, searchTerm, filterStatus, sortBy, sortDirection]);

  // Email auto-cache with callback to update state
  const handleEmailUpdate = useCallback((newEmails) => {
    console.log('📧 [MAIL VIEW] Email data updated automatically:', newEmails.length);
    setEmails(newEmails);
  }, []);

  // Setup automatic cache management
  const { manualRefresh: manualRefreshEmails } = useAutoCache({
    type: 'emails',
    enabled: true,
    onDataUpdate: handleEmailUpdate,
    checkInterval: 60000 // Check every 60 seconds
  });

  const loadEmails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getEmails(true); // Use intelligent caching in mail view
      setEmails(response.value || []);
      console.log('📧 [MAIL VIEW] Emails loaded:', response.value?.length || 0);
    } catch (error) {
      console.error('📧 [MAIL VIEW] Error loading emails:', error);
      if (error.response?.status === 401) {
        setError('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
        setTimeout(() => {
          try {
            const raw = String(document.cookie || '');
            const allowAutoSSO = raw.split(';').some(c => c.trim().startsWith('was_logged_in=1'));
            window.location.href = allowAutoSSO ? '/auth/sso' : '/auth/login';
          } catch (_) {
            window.location.href = '/auth/login';
          }
        }, 3000);
      } else {
        setError('Fehler beim Laden der E-Mails. Bitte versuchen Sie es später erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    console.log('📧 [MAIL VIEW] Manual refresh triggered');
    setLoading(true);
    try {
      await manualRefreshEmails();
    } catch (error) {
      console.error('📧 [MAIL VIEW] Manual refresh error:', error);
      setError('Fehler beim Aktualisieren der E-Mails');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailReadUpdate = useCallback(async () => {
    console.log('📧 [MAIL VIEW] Email read status changed, refreshing...');
    try {
      await manualRefreshEmails();
    } catch (error) {
      console.error('📧 [MAIL VIEW] Error refreshing emails after read status change:', error);
    }
  }, [manualRefreshEmails]);

  const filterAndSortEmails = () => {
    let filtered = [...emails];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(email => 
        email.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.from?.emailAddress?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.from?.emailAddress?.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.bodyPreview?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      switch (filterStatus) {
        case 'unread':
          filtered = filtered.filter(email => !email.isRead);
          break;
        case 'read':
          filtered = filtered.filter(email => email.isRead);
          break;
        case 'flagged':
          filtered = filtered.filter(email => email.flag && email.flag.flagStatus === 'flagged');
          break;
        default:
          break;
      }
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'subject':
          aValue = a.subject || '';
          bValue = b.subject || '';
          break;
        case 'from':
          aValue = a.from?.emailAddress?.name || a.from?.emailAddress?.address || '';
          bValue = b.from?.emailAddress?.name || b.from?.emailAddress?.address || '';
          break;
        case 'received':
        default:
          aValue = new Date(a.receivedDateTime);
          bValue = new Date(b.receivedDateTime);
          break;
      }

      if (sortDirection === 'desc') {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      } else {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
    });

    setFilteredEmails(filtered);
  };

  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(newSortBy);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortDirection === 'desc' ? <ChevronDown size={16} /> : <ChevronUp size={16} />;
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <RefreshCw className="animate-spin" size={32} />
          <p>Lade E-Mails...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="mail-view">
        <div className="mail-header">
          <h2>
            <Mail size={24} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            E-Mails
            {filteredEmails.length > 0 && (
              <span className="email-count"> ({filteredEmails.length})</span>
            )}
          </h2>
          <div className="mail-actions">
            <button 
              onClick={handleRefresh}
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <RefreshCw size={16} />
              Aktualisieren
            </button>
            
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <Filter size={16} />
              Filter
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="filter-panel">
            <div className="filter-group">
              <label>Suchen:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="E-Mails suchen..."
                className="filter-input"
              />
            </div>
            
            <div className="filter-group">
              <label>Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">Alle</option>
                <option value="unread">Ungelesen</option>
                <option value="read">Gelesen</option>
                <option value="flagged">Markiert</option>
              </select>
            </div>
          </div>
        )}

        <div className="sort-controls">
          <span>Sortieren nach:</span>
          <button 
            onClick={() => handleSort('received')}
            className={`sort-btn ${sortBy === 'received' ? 'active' : ''}`}
          >
            Empfangen {getSortIcon('received')}
          </button>
          <button 
            onClick={() => handleSort('subject')}
            className={`sort-btn ${sortBy === 'subject' ? 'active' : ''}`}
          >
            Betreff {getSortIcon('subject')}
          </button>
          <button 
            onClick={() => handleSort('from')}
            className={`sort-btn ${sortBy === 'from' ? 'active' : ''}`}
          >
            Absender {getSortIcon('from')}
          </button>
        </div>

        <EmailList 
          emails={filteredEmails} 
          onEmailSelect={setSelectedEmail}
          selectedEmail={selectedEmail}
          onEmailUpdate={handleEmailReadUpdate}
        />
      </div>
    </div>
  );
};

export default MailView; 